import { useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LeadCapture from "../components/LeadCapture";
import HealthQuestionnaire from "../components/HealthQuestionnaire";
import QuietRoomCheck from "../components/QuietRoomCheck";
import HeadphonesCheck from "../components/HeadphonesCheck";
import VolumeCalibration from "../components/VolumeCalibration";
import FrequencyTest from "../components/FrequencyTest";
import Results from "../components/Results";
import ProgressBar from "../components/ProgressBar";

const LOGO_URL = "/logo.png";

const FREQUENCIES = [250, 500, 1000, 2000, 4000, 8000];

const STEPS = {
  LEAD_CAPTURE: 0,
  HEALTH_QUESTIONNAIRE: 1,
  QUIET_ROOM: 2,
  HEADPHONES: 3,
  CALIBRATION_1: 4,
  CALIBRATION_2: 5,
  LEFT_EAR: 6,
  RIGHT_EAR: 7,
  RESULTS: 8,
};

const TOTAL_STEPS = 9;

// Animation variants extracted as constant to avoid recreation
const PAGE_VARIANTS = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const TRANSITION_CONFIG = { duration: 0.3 };

// Helper to create initial results
const createInitialResults = () => 
  FREQUENCIES.map((freq) => ({ frequency: freq, db_level: 50 }));

// Health Warning Component
function HealthWarning({ onContinue, onBack }) {
  return (
    <div className="bg-[#111827] border border-[#1E2D3D] rounded-2xl p-6 sm:p-8 max-w-xl mx-auto">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-[#FBBF24]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-[#FBBF24]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 className="text-2xl sm:text-3xl font-medium text-[#F8FAFC] mb-3 font-['Outfit']">
          We Recommend Seeing a Specialist
        </h2>
        <p className="text-[#94A3B8]">
          Based on your answers, we recommend you see an ENT doctor or audiologist
          before proceeding with the hearing test.
        </p>
      </div>

      <a
        href="https://buyhearingaid.com/pages/contact-us"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between bg-[#1E293B] border border-[#334155] rounded-xl p-4 mb-6 hover:border-[#22D3EE] transition-colors group"
        data-testid="contact-link"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#22D3EE]/10 rounded-lg flex items-center justify-center">
            <svg
              className="w-5 h-5 text-[#22D3EE]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <div>
            <p className="text-[#F8FAFC] font-medium">Contact Us</p>
            <p className="text-[#64748B] text-sm">Get professional assistance</p>
          </div>
        </div>
        <svg
          className="w-5 h-5 text-[#64748B] group-hover:text-[#22D3EE] transition-colors"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </a>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-3 px-6 rounded-lg border border-[#334155] text-[#F8FAFC] font-medium hover:bg-[#1E293B] transition-colors"
          data-testid="warning-back-btn"
        >
          Go Back
        </button>
        <button
          onClick={onContinue}
          className="flex-1 py-3 px-6 rounded-lg bg-[#22D3EE] text-[#0A0F1C] font-semibold hover:bg-[#67E8F9] transition-colors"
          data-testid="warning-continue-btn"
        >
          Proceed to Test
        </button>
      </div>
    </div>
  );
}

// Step wrapper component to reduce repetition
function StepWrapper({ stepKey, children }) {
  return (
    <motion.div
      key={stepKey}
      variants={PAGE_VARIANTS}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={TRANSITION_CONFIG}
    >
      {children}
    </motion.div>
  );
}

export default function HearingTest() {
  const [currentStep, setCurrentStep] = useState(STEPS.LEAD_CAPTURE);
  const [leadData, setLeadData] = useState(null);
  const [healthFlags, setHealthFlags] = useState({});
  const [showWarning, setShowWarning] = useState(false);
  const [leftEarResults, setLeftEarResults] = useState(createInitialResults);
  const [rightEarResults, setRightEarResults] = useState(createInitialResults);

  const handleLeadSubmit = useCallback((data) => {
    setLeadData(data);
    setCurrentStep(STEPS.HEALTH_QUESTIONNAIRE);
  }, []);

  const handleHealthSubmit = useCallback((flags) => {
    setHealthFlags(flags);
    const hasWarnings = Object.values(flags).some((v) => v === true);
    if (hasWarnings) {
      setShowWarning(true);
    } else {
      setCurrentStep(STEPS.QUIET_ROOM);
    }
  }, []);

  const handleContinueAfterWarning = useCallback(() => {
    setShowWarning(false);
    setCurrentStep(STEPS.QUIET_ROOM);
  }, []);

  const handleQuietRoomConfirm = useCallback(() => {
    setCurrentStep(STEPS.HEADPHONES);
  }, []);

  const handleHeadphonesConfirm = useCallback(() => {
    setCurrentStep(STEPS.CALIBRATION_1);
  }, []);

  const handleCalibration1Next = useCallback(() => {
    setCurrentStep(STEPS.CALIBRATION_2);
  }, []);

  const handleCalibration2Next = useCallback(() => {
    setCurrentStep(STEPS.LEFT_EAR);
  }, []);

  const handleLeftEarComplete = useCallback((results) => {
    setLeftEarResults(results);
    setCurrentStep(STEPS.RIGHT_EAR);
  }, []);

  const handleRightEarComplete = useCallback((results) => {
    setRightEarResults(results);
    setCurrentStep(STEPS.RESULTS);
  }, []);

  const handleRetakeTest = useCallback(() => {
    setCurrentStep(STEPS.LEAD_CAPTURE);
    setLeadData(null);
    setHealthFlags({});
    setShowWarning(false);
    setLeftEarResults(createInitialResults());
    setRightEarResults(createInitialResults());
  }, []);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      if (showWarning) {
        setShowWarning(false);
      } else {
        setCurrentStep((prev) => prev - 1);
      }
    }
  }, [currentStep, showWarning]);

  // Memoize current step content to avoid unnecessary recalculations
  const stepContent = useMemo(() => {
    if (currentStep === STEPS.LEAD_CAPTURE) {
      return (
        <StepWrapper stepKey="lead-capture">
          <LeadCapture onSubmit={handleLeadSubmit} />
        </StepWrapper>
      );
    }

    if (currentStep === STEPS.HEALTH_QUESTIONNAIRE && !showWarning) {
      return (
        <StepWrapper stepKey="health-questionnaire">
          <HealthQuestionnaire onSubmit={handleHealthSubmit} onBack={goBack} />
        </StepWrapper>
      );
    }

    if (showWarning) {
      return (
        <StepWrapper stepKey="warning">
          <HealthWarning onContinue={handleContinueAfterWarning} onBack={goBack} />
        </StepWrapper>
      );
    }

    if (currentStep === STEPS.QUIET_ROOM) {
      return (
        <StepWrapper stepKey="quiet-room">
          <QuietRoomCheck onConfirm={handleQuietRoomConfirm} onBack={goBack} />
        </StepWrapper>
      );
    }

    if (currentStep === STEPS.HEADPHONES) {
      return (
        <StepWrapper stepKey="headphones">
          <HeadphonesCheck onConfirm={handleHeadphonesConfirm} onBack={goBack} />
        </StepWrapper>
      );
    }

    if (currentStep === STEPS.CALIBRATION_1) {
      return (
        <StepWrapper stepKey="calibration-1">
          <VolumeCalibration step={1} onNext={handleCalibration1Next} onBack={goBack} />
        </StepWrapper>
      );
    }

    if (currentStep === STEPS.CALIBRATION_2) {
      return (
        <StepWrapper stepKey="calibration-2">
          <VolumeCalibration step={2} onNext={handleCalibration2Next} onBack={goBack} />
        </StepWrapper>
      );
    }

    if (currentStep === STEPS.LEFT_EAR) {
      return (
        <StepWrapper stepKey="left-ear">
          <FrequencyTest
            ear="left"
            frequencies={FREQUENCIES}
            onComplete={handleLeftEarComplete}
            onBack={goBack}
          />
        </StepWrapper>
      );
    }

    if (currentStep === STEPS.RIGHT_EAR) {
      return (
        <StepWrapper stepKey="right-ear">
          <FrequencyTest
            ear="right"
            frequencies={FREQUENCIES}
            onComplete={handleRightEarComplete}
            onBack={goBack}
          />
        </StepWrapper>
      );
    }

    if (currentStep === STEPS.RESULTS) {
      return (
        <StepWrapper stepKey="results">
          <Results
            leadData={leadData}
            leftEarResults={leftEarResults}
            rightEarResults={rightEarResults}
            healthFlags={healthFlags}
            onRetake={handleRetakeTest}
          />
        </StepWrapper>
      );
    }

    return null;
  }, [
    currentStep,
    showWarning,
    leadData,
    leftEarResults,
    rightEarResults,
    healthFlags,
    handleLeadSubmit,
    handleHealthSubmit,
    handleContinueAfterWarning,
    handleQuietRoomConfirm,
    handleHeadphonesConfirm,
    handleCalibration1Next,
    handleCalibration2Next,
    handleLeftEarComplete,
    handleRightEarComplete,
    handleRetakeTest,
    goBack,
  ]);

  return (
    <div className="min-h-screen bg-[#0A0F1C]" data-testid="hearing-test-app">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0F1C]/95 backdrop-blur-sm border-b border-[#1E2D3D]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img
            src={LOGO_URL}
            alt="BuyHearingAid.com"
            className="h-10 sm:h-12"
            data-testid="logo"
          />
          {currentStep < STEPS.RESULTS && (
            <ProgressBar current={currentStep + 1} total={TOTAL_STEPS} />
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {stepContent}
          </AnimatePresence>
        </div>
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-[#0A0F1C]/95 backdrop-blur-sm border-t border-[#1E2D3D] py-3">
        <p className="text-center text-[#64748B] text-xs px-4">
          This online hearing test is for educational purposes only and does not replace a professional evaluation.
        </p>
      </footer>
    </div>
  );
}
